'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useEffect, useState, useRef } from 'react'

export interface LogoStripItem {
  name: string
  logoUrl?: string
  link?: string
}

interface LogoStripProps {
  title: string
  items: LogoStripItem[]
  showCta?: boolean
  ctaText?: string
  ctaLink?: string
  emptyStateText?: string
  autoRotate?: boolean
}

/**
 * Reusable logo strip component for displaying teams, sponsors, or partners
 * Features:
 * - Automatic carousel rotation with center highlighting
 * - Smooth transitions between logos
 * - Pause on hover
 * - Optional CTA button
 */
export function LogoStrip({
  title,
  items,
  showCta = false,
  ctaText,
  ctaLink,
  emptyStateText,
  autoRotate = true,
}: LogoStripProps) {
  const hasItems = items.length > 0
  const [centerIndex, setCenterIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-rotate through logos
  useEffect(() => {
    if (!autoRotate || !hasItems || isPaused) return

    const interval = setInterval(() => {
      setCenterIndex(prev => (prev + 1) % items.length)
    }, 3000) // Change every 3 seconds

    return () => clearInterval(interval)
  }, [autoRotate, hasItems, items.length, isPaused])

  // Smooth scroll to centered item
  useEffect(() => {
    if (!scrollRef.current) return

    const container = scrollRef.current
    const logoWidth = 128 // 96px (base size) + 32px gap
    const containerWidth = container.offsetWidth
    const scrollPosition = centerIndex * logoWidth - containerWidth / 2 + 64

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    })
  }, [centerIndex])

  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-5xl px-6">
        <h2 className="text-center text-3xl font-semibold text-navy">{title}</h2>

        {hasItems ? (
          <div className="mt-16">
            <div
              ref={scrollRef}
              className="relative overflow-x-auto scrollbar-hide py-4"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="flex gap-8 px-8 pb-4">
                {items.map((item, index) => (
                  <LogoStripItem
                    key={`${item.name}-${index}`}
                    item={item}
                    isCenter={index === centerIndex}
                  />
                ))}
              </div>
            </div>
            {/* Carousel dots */}
            <div className="mt-6 flex justify-center gap-2">
              {items.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCenterIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === centerIndex ? 'w-8 bg-navy' : 'w-2 bg-storm/30'
                  }`}
                  aria-label={`Go to logo ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-10 text-center">
            <p className="text-storm">{emptyStateText || 'Coming soon'}</p>
          </div>
        )}

        {showCta && ctaText && ctaLink && (
          <div className="mt-10 text-center">
            <Link
              href={ctaLink}
              className="inline-block rounded-md border border-navy px-6 py-3 font-medium text-navy transition-colors hover:bg-navy hover:text-white"
            >
              {ctaText}
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

function LogoStripItem({ item, isCenter }: { item: LogoStripItem; isCenter: boolean }) {
  const content = item.logoUrl ? (
    <div
      className={`flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-white p-6 transition-all duration-500 ${
        isCenter
          ? 'scale-125 border-navy/40 shadow-lg'
          : 'scale-100 border-storm/10 shadow-sm opacity-50'
      }`}
    >
      <div
        className={`relative h-full w-full transition-opacity duration-500 ${isCenter ? 'opacity-100' : 'opacity-60'}`}
      >
        <Image src={item.logoUrl} alt={item.name} fill className="object-contain rounded-full" />
      </div>
    </div>
  ) : (
    <div
      className={`flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed bg-white transition-all duration-500 ${
        isCenter
          ? 'scale-125 border-navy/40 shadow-lg opacity-100'
          : 'scale-100 border-storm/20 opacity-50'
      }`}
    >
      <span className="text-center text-xs font-medium text-storm px-3">{item.name}</span>
    </div>
  )

  if (item.link) {
    // Check if link is external (starts with http)
    const isExternal = item.link.startsWith('http')

    if (isExternal) {
      return (
        <a href={item.link} target="_blank" rel="noopener noreferrer" className="block">
          {content}
        </a>
      )
    }

    return (
      <Link href={item.link} className="block">
        {content}
      </Link>
    )
  }

  return content
}
